import * as pmath from './pmath.js';
import { BoxColliderField } from './worldgen/boxColliders.js';
import type { RaceBox, RaceCourse, RaceVec3 } from './raceCourse.js';
import {raceBoxExtents,raceBoxBasis,raceBoxCollider,raceBoxLocal,type RaceBasis} from './raceGeometry.js';
import {raceArenaWalls} from './raceEncounter.js';

/** Deterministic seconds since the round started, shared by every racer. */
export interface RaceShutter {
  id: string;
  sectionId: string;
  center: RaceVec3;
  /** Clear opening width/height and solid leaf depth. */
  size: RaceVec3;
  yaw: number;
  basis?: RaceBasis;
  period: number;
  openSeconds: number;
  transitionSeconds: number;
  phase: number;
  /** Reference time from jumping off the preceding deck to this opening. */
  crossingSeconds: number;
}

const phaseAt = (s: RaceShutter, time: number) => ((time + s.phase) % s.period + s.period) % s.period;
const smooth = (t: number) => { t = Math.max(0, Math.min(1, t)); return t * t * (3 - 2 * t); };

export function raceShutterPose(shutter: RaceShutter, timeSeconds: number): { opening: number; warning: boolean; panels: RaceBox[] } {
  const s = shutter, phase = phaseAt(s, timeSeconds), closingEnd = s.openSeconds + s.transitionSeconds;
  const opening = phase <= s.openSeconds ? 1 : phase < closingEnd
    ? 1 - smooth((phase - s.openSeconds) / s.transitionSeconds)
    : phase >= s.period - s.transitionSeconds ? smooth((phase - s.period + s.transitionSeconds) / s.transitionSeconds) : 0;
  const [right] = raceBoxBasis(s);
  const panels: RaceBox[] = [-1, 1].map(side => {
    const x = side * (s.size[0] / 4 + opening * (s.size[0] / 2 + .35));
    return { id: `${s.id}-${side < 0 ? 'left' : 'right'}`, kind: 'obstacle',
      center: s.center.map((v,i)=>v+right[i]*x) as RaceVec3,
      size: [s.size[0] / 2, s.size[1], s.size[2]], yaw: s.yaw, basis:s.basis, standable: false };
  });
  return { opening, warning: phase >= s.openSeconds - .75 && phase < closingEnd, panels };
}

/** A full-open interval, including its endpoints; used for safe deck departure. */
export function raceShutterSafeWindow(s: RaceShutter, start: number, end: number): boolean {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start || end - start > s.openSeconds) return false;
  const phase = phaseAt(s, start);
  return phase <= s.openSeconds && phase + end - start <= s.openSeconds;
}

function addBox(field: BoxColliderField, radius: number, box: RaceBox): void {
  field.addBox(raceBoxCollider(box,radius));
}

/** Keep the static field intact; rebuild only the few moving leaves. */
export class RaceCourseColliders extends BoxColliderField {
  private moving: BoxColliderField;
  private positions = '';
  constructor(private readonly course: RaceCourse) {
    super(course.radius);
    this.moving = new BoxColliderField(course.radius);
    for (const box of course.boxes) addBox(this, course.radius, box);
    this.update(0);
  }
  update(timeSeconds: number): void {
    const panels = this.course.shutters.flatMap(s => raceShutterPose(s, timeSeconds).panels);
    panels.push(...raceArenaWalls(this.course,timeSeconds));
    const positions = panels.map(p => `${p.center.join(',')},${p.yaw}`).join(';');
    if (positions === this.positions) return;
    this.positions = positions;
    this.moving = new BoxColliderField(this.course.radius);
    for (const panel of panels) addBox(this.moving, this.course.radius, panel);
  }
  override get featureCount(): number { return super.featureCount + this.moving.featureCount; }
  override standRadius(px:number,py:number,pz:number):number|null{return this.standRadiusBelow(px,py,pz,Infinity);}
  override standRadiusBelow(px:number,py:number,pz:number,maxR:number):number|null{
    const a=super.standRadiusBelow(px,py,pz,maxR),b=this.moving.standRadiusBelow(px,py,pz,maxR);
    return a===null?b:b===null?a:Math.max(a,b);
  }
  override resolve(px: number, py: number, pz: number, radius: number, out: { x: number; y: number; z: number }): boolean {
    const fixed = super.resolve(px, py, pz, radius, out);
    const moving = this.moving.resolve(fixed ? out.x : px, fixed ? out.y : py, fixed ? out.z : pz, radius, out);
    return fixed || moving;
  }
  override resolveBrute(px: number, py: number, pz: number, radius: number, out: { x: number; y: number; z: number }): boolean {
    const fixed = super.resolveBrute(px, py, pz, radius, out);
    const moving = this.moving.resolveBrute(fixed ? out.x : px, fixed ? out.y : py, fixed ? out.z : pz, radius, out);
    return fixed || moving;
  }
  override occludes(px: number, py: number, pz: number, radius = 0): boolean {
    return super.occludes(px, py, pz, radius) || this.moving.occludes(px, py, pz, radius);
  }
  override clearanceRadius(dx: number, dy: number, dz: number, baseRadius: number, probeRadius = 0): number {
    return Math.max(super.clearanceRadius(dx, dy, dz, baseRadius, probeRadius), this.moving.clearanceRadius(dx, dy, dz, baseRadius, probeRadius));
  }
  override segmentHit(ax: number, ay: number, az: number, bx: number, by: number, bz: number, radius = 0): number {
    return firstHit(super.segmentHit(ax, ay, az, bx, by, bz, radius), this.moving.segmentHit(ax, ay, az, bx, by, bz, radius));
  }
  override segmentHitBrute(ax: number, ay: number, az: number, bx: number, by: number, bz: number, radius = 0): number {
    return firstHit(super.segmentHitBrute(ax, ay, az, bx, by, bz, radius), this.moving.segmentHitBrute(ax, ay, az, bx, by, bz, radius));
  }
  override segmentCast(ax: number, ay: number, az: number, bx: number, by: number, bz: number, radius = 0): { t: number; n: RaceVec3 } | null {
    const a = super.segmentCast(ax, ay, az, bx, by, bz, radius), b = this.moving.segmentCast(ax, ay, az, bx, by, bz, radius);
    return !a ? b : !b || a.t <= b.t ? a : b;
  }
}
const firstHit = (a: number, b: number) => a < 0 ? b : b < 0 ? a : Math.min(a, b);

/** Capsule feet sweep against an oriented leaf, including head/body clearance. */
function sweepLeaf(panel: RaceBox, from: RaceVec3, to: RaceVec3, motionPadding: number, bodyRadius = .46, feetClearance = .15): boolean {
  const extent=raceBoxExtents(panel);
  for(let axis=0;axis<3;axis++){
    const padding=axis===1?1.82+motionPadding:bodyRadius+motionPadding;
    if(Math.min(from[axis],to[axis])-padding>panel.center[axis]+extent[axis]
      ||Math.max(from[axis],to[axis])+padding<panel.center[axis]-extent[axis])return false;
  }
  const axes=raceBoxBasis(panel),a=raceBoxLocal(panel,from),b=raceBoxLocal(panel,to);
  // Project the upright player's clearance onto each hull axis, including
  // tilted ceilings/floors. Yaw-only panels retain the established clearance.
  const min=axes.map((axis,i)=>-panel.size[i]/2-Math.max(axis[1]*feetClearance,axis[1]*1.8)
    -pmath.hypot(axis[0],axis[2])*bodyRadius-(i===0?motionPadding:0));
  const max=axes.map((axis,i)=>panel.size[i]/2-Math.min(axis[1]*feetClearance,axis[1]*1.8)
    +pmath.hypot(axis[0],axis[2])*bodyRadius+(i===0?motionPadding:0));
  let enter = 0, leave = 1;
  for (let axis = 0; axis < 3; axis++) {
    const d = b[axis] - a[axis];
    if (Math.abs(d) < 1e-9) { if (a[axis] < min[axis] || a[axis] > max[axis]) return false; }
    else {
      const t1 = (min[axis] - a[axis]) / d, t2 = (max[axis] - a[axis]) / d;
      enter = Math.max(enter, Math.min(t1, t2)); leave = Math.min(leave, Math.max(t1, t2));
      if (enter > leave) return false;
    }
  }
  return true;
}

/** Continuous player sweep, with conservative moving-leaf coverage per 25ms. */
export function hitRaceShutter(course: RaceCourse, from: RaceVec3, to: RaceVec3, start: number, end: number): boolean {
  const duration = Math.max(0, end - start), steps = Math.max(1, Math.min(128, Math.ceil(duration / .025)));
  const point = (t: number): RaceVec3 => [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t, from[2] + (to[2] - from[2]) * t];
  for (const shutter of course.shutters) {
    const back=raceBoxBasis(shutter)[2];
    const z0 = from.reduce((sum,v,i)=>sum+(v-shutter.center[i])*back[i],0);
    const z1 = to.reduce((sum,v,i)=>sum+(v-shutter.center[i])*back[i],0);
    const reach = shutter.size[2] / 2 + .46+Math.abs(back[1])*1.8;
    if (Math.min(z0, z1) > reach || Math.max(z0, z1) < -reach) continue;
    for (let i = 0; i < steps; i++) {
      const t0 = start + duration * i / steps, t1 = start + duration * (i + 1) / steps;
      const a = raceShutterPose(shutter, t0), b = raceShutterPose(shutter, t1), mid = raceShutterPose(shutter, (t0 + t1) / 2);
      const padding = Math.max(Math.abs(a.opening - mid.opening), Math.abs(b.opening - mid.opening)) * (shutter.size[0] / 2 + .35);
      if (mid.panels.some(panel => sweepLeaf(panel, point(i / steps), point((i + 1) / steps), padding))) return true;
    }
  }
  return false;
}

const tunnelPanels = new WeakMap<RaceCourse, { boxes: RaceBox[]; panels: RaceBox[] }>();

/** Contact with any tunnel surface fails the attempt, including a swept graze.
 * The 1cm lateral tolerance includes the controller's .45m capsule push-out,
 * so resolving a collision cannot hide contact from the race rule. */
export function hitRaceTunnel(course: RaceCourse, from: RaceVec3, to: RaceVec3, wallAttached = false): boolean {
  let cached = tunnelPanels.get(course);
  if (!cached || cached.boxes !== course.boxes) {
    cached = { boxes: course.boxes, panels: course.boxes.filter(box => box.kind === 'tunnel') };
    tunnelPanels.set(course, cached);
  }
  // An attached rig holds its root .78–.88m from a wall while the hands/body
  // touch it. Count that contact too; ordinary flight retains capsule clearance.
  return cached.panels.some(panel => sweepLeaf(panel, from, to, 0, wallAttached && (panel.basis ? Math.abs(panel.basis[1][1]) < .7 : panel.size[1] > panel.size[0]) ? .94 : .46, -.02));
}

const hazardPanels = new WeakMap<RaceCourse, {boxes: RaceBox[]; panels: RaceBox[]}>();
/** Fatal red surfaces. Wall bands include the rig's attached wall-run gap. */
export function hitRaceHazard(course: RaceCourse, from: RaceVec3, to: RaceVec3, wallAttached = false): boolean {
  if (hitRaceTunnel(course, from, to, wallAttached)) return true;
  let cached = hazardPanels.get(course);
  if (!cached || cached.boxes !== course.boxes) {
    cached = {boxes: course.boxes, panels: course.boxes.filter(box => box.kind === 'wall-hazard' || box.kind === 'dive-hazard')};
    hazardPanels.set(course, cached);
  }
  return cached.panels.some(panel => sweepLeaf(panel, from, to, 0, panel.kind === 'wall-hazard' || wallAttached && (panel.basis ? Math.abs(panel.basis[1][1]) < .7 : panel.size[1] > panel.size[0]) ? .94 : .46, -.02));
}
